/**
 * ArgemiroPropuestasApiClient -- el SI del dueno sobre lo que Argemiro propone.
 *
 * MISION CIERRE-CONTEO-V1-001, tarea CC-BOTON-T2.
 *
 * Fuente: POST /api/v1/argemiro/propuestas/decidir, publicado por Argy-A en
 * [CC-DECIDIR-T1] (nexo-api PR #174, migracion aplicada). Las claves de aqui son las
 * de ESE contrato.
 *
 * EL CUERPO SE ARMA AQUI, no en la vista: un solo sitio conoce el contrato. Y va en
 * camelCase porque ASP.NET liga sin distinguir mayusculas pero NO quita guiones bajos:
 * un snake_case no liga con nada y el campo llega vacio SIN dar error. Al leer, en
 * cambio, se toleran las dos formas: la lectura perdona, la escritura no.
 *
 * FAIL-OPEN: si el endpoint aun no esta desplegado, esto devuelve un motivo legible en
 * vez de romperse, y la pantalla lo dice con su causa. El dia que se mergee, se
 * enciende solo SIN re-deploy del panel.
 *
 * @module infrastructure/api/ArgemiroPropuestasApiClient
 */

import { ApiBase } from './ApiBase.js';

const ENDPOINT = '/api/v1/argemiro/propuestas/decidir';

/** Lo que el dueno puede responder. El nombre tecnico NUNCA llega a la pantalla. */
export const DECISIONES = { ACEPTAR: 'aceptar', RECHAZAR: 'rechazar', REVISAR: 'revisar' };

const pick = (o, ...claves) => {
  for (const k of claves) if (o && o[k] !== undefined && o[k] !== null) return o[k];
  return null;
};

export const ArgemiroPropuestasApiClient = {
  /**
   * Registra la decision del dueno sobre una propuesta. Nunca lanza.
   *
   * @param {object} p
   * @param {string} p.businessId
   * @param {string} p.propuestaKey  llave determinista: decidir dos veces no duplica
   * @param {string} p.tipo          cruce_error_conteo | entrada_sin_registrar | umbral
   * @param {string} p.decision      aceptar | rechazar | revisar
   * @returns {Promise<{ok:boolean, data?:object, motivo?:string, detalle?:string}>}
   */
  async decidir(p = {}) {
    if (!p.businessId || !p.propuestaKey || !p.tipo || !p.decision) {
      return { ok: false, motivo: 'FALTAN_DATOS', detalle: 'la propuesta llego incompleta' };
    }

    // Solo se mandan las claves que el DTO acepta, y en camelCase.
    const body = {
      businessId: p.businessId,
      propuestaKey: p.propuestaKey,
      tipo: p.tipo,
      decision: p.decision
    };
    if (p.nota) body.nota = p.nota;
    if (p.insumoId) body.insumoId = p.insumoId;
    if (p.insumoNombre) body.insumoNombre = p.insumoNombre;
    if (p.fechaFalta) body.fechaFalta = p.fechaFalta;
    if (p.fechaSobra) body.fechaSobra = p.fechaSobra;
    if (p.cantidad !== null && p.cantidad !== undefined) body.cantidad = Number(p.cantidad);

    let res;
    try {
      res = await ApiBase.post(ENDPOINT, body);
    } catch (err) {
      return { ok: false, motivo: 'SIN_RED', detalle: err?.message || 'no se pudo hablar con la API' };
    }

    if (!res.ok) {
      // El motivo importa: un mensaje unico para todo hace dudar de lo que si funciona.
      const txt = JSON.stringify(res.error || '');
      if (/not\s*found|404/i.test(txt)) {
        return { ok: false, motivo: 'NO_PUBLICADO', detalle: 'la decision todavia no esta publicada (nexo-api PR #174)' };
      }
      if (/401|unauthor/i.test(txt)) return { ok: false, motivo: 'SIN_SESION', detalle: 'tu sesion no puede decidir' };
      if (/403|forbidden/i.test(txt)) return { ok: false, motivo: 'PROHIBIDO', detalle: 'solo el dueno decide esto' };
      if (/409|conflict/i.test(txt)) {
        return { ok: false, motivo: 'YA_DECIDIDA', detalle: 'esta propuesta ya tenia una decision' };
      }
      return {
        ok: false,
        motivo: 'ERROR_API',
        detalle: (typeof res.error === 'string' ? res.error : res.error?.message) || 'error de la API'
      };
    }

    const d = res.data?.data ?? res.data ?? {};
    return {
      ok: true,
      data: {
        id: pick(d, 'id'),
        propuestaKey: pick(d, 'propuestaKey', 'propuesta_key'),
        tipo: pick(d, 'tipo'),
        decision: pick(d, 'decision')
      }
    };
  }
};
